import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Storage } from '../core/storage.service';
import * as differenceInSeconds from 'date-fns/difference_in_seconds';
import { Router } from '@angular/router';
import { interval } from 'rxjs/observable/interval';

@Injectable()
export class AuthService {
  private user: any;
  private tokenSubscription: any;
  constructor(private api: ApiService, private router: Router) {
    this.startTokenCheck();
  }

  public startTokenCheck() {
    this.checkToken();
    // checking token every 10 seconds, since we are dealing with short sessions (2 mins)
    this.tokenSubscription = interval(10000).subscribe(() => {
      this.checkToken();
    });
  }

  public getLogin(session: string) {
    return this.api.get('/login', null, 'auth', {
      'Authorization': `SessionKey ${session}`
    });
  }

  public postCheckPassword(password: string) {
    return this.api.get('/check-password', null, 'auth');
  }

  public postLogin(login: string, password: string) {
    return this.api.post(
      '/login',
      {
        username: login,
        password: password
      },
      'auth'
    );
  }

  public postLogout() {
    if (this.getUser()) {
      const call = this.api.post('/logout', {}, 'auth');
      call.subscribe(res => {});
      Storage.del('embed-user');
      this.user = undefined;
      return call;
    }
    return new Observable(observer => {
      observer.error(new Error('No user logged in'));
      observer.complete();
    });
  }

  public postRequest(loginOrRegistration: string | any) {
    let _payload = loginOrRegistration;
    if (typeof loginOrRegistration === 'string') {
      _payload = {
        username: loginOrRegistration
      };
    }
    return this.api.post('/donor/request', _payload, 'auth');
  }

  public postResetPassword(email: string) {
    return this.api.post('/reset-password/request', {email}, 'auth');
  }

  public postValidate(token: string, code: string) {
    return this.api.post(
      `/token/${token}/verify`,
      {
        token: token,
        code: code
      },
      'auth'
    );
  }

  public setUser(user) {
    const updated = new Date();
    const userdata = Object.assign({}, user, { updated: updated });
    Storage.set('embed-user', userdata);
    this.user = userdata;
  }

  public getUser() {
    let user = this.user;
    if (!user) {
      user = Storage.get('embed-user');
    }
    return user;
  }

  public getContact() {
    return this.api.get('contact', null, 'donor/donor/', {
      'Authorization': `SessionKey ${this.getToken()}`
    });
  }

  public getPaymentMethods() {
    return this.api.get('payment-methods', null, 'donor/donor/', {
      'Authorization': `SessionKey ${this.getToken()}`
    });
  }

  getToken() {
    let user = this.user;
    if (!user) {
      user = Storage.get('embed-user');
    }
    return user ? user.session_key : null;
  }

  updateToken() {
    const currentUser = this.getUser();
    const headers = {'Authorization': `SessionKey ${currentUser.session_key}`};

    this.api.get('/session', null, 'auth', headers).subscribe(
      (data: any) => {
        const newExpire = data.expires_at || new Date().toISOString();
        const user = Object.assign({}, currentUser, {
          session_expires_at: newExpire
        });
        this.setUser(user);
      },
      err => {
        // if unable to update token unsubscribe from observable
        this.postLogout().subscribe(() => {
          this.tokenSubscription.unsubscribe();
          this.router.navigate(['/']);
        });
      }
    );
  }

  public checkToken() {
    // if the user exist keep checking token
    const currentUser = this.getUser();
    // dev users do not have session expire
    if (!currentUser || !currentUser.session_expires_at) {
      return;
    }

    const expDate = currentUser.session_expires_at || new Date().toISOString();

    const sessionExpire = new Date(expDate);

    const timeRemaining = differenceInSeconds(sessionExpire, new Date());

    if (timeRemaining < 20) {
      // if less than 20 seconds left, refresh the token and update the user ls
      this.updateToken();
    }
  }
}
